import { Circle, TrendingDown, TrendingUp } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components";
import { TableErrorState } from "../../../../components/table";
import { Row } from "../../../../components/row";
import type { TargetOpportunityModel } from "../../types/TargetOppotunity";
import { TableRowsSkeleton } from "../region/region-skeletons";

interface TargetOpportunityTableProps {
  data: TargetOpportunityModel[] | null;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const TargetOpportunityTable = ({
  data: targetOpportunities,
  isLoading = false,
  error,
  onRetry,
}: TargetOpportunityTableProps) => {
  const rows = (targetOpportunities || [])
    .slice()
    .sort((a: TargetOpportunityModel, b: TargetOpportunityModel) => {
      const nameA = (a?.area_name || "").toLowerCase();
      const nameB = (b?.area_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((data: TargetOpportunityModel) => {
      const growthRate = data?.growth_rate;
      return (
        <TableRow
          key={data?.organization_population_id}
          className="hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <TableCell className="text-gray-900 dark:text-white">{data?.area_name}</TableCell>
          <TableCell className="text-center text-gray-900 dark:text-white">
            {data?.total_events}
          </TableCell>
          <TableCell className="text-center text-gray-900 dark:text-white">
            {data?.total_happening_events}
          </TableCell>
          <TableCell className="text-gray-900 dark:text-white">
            <div className="flex justify-center items-center">
              <span>{data?.attendance_rate?.toFixed(2)}%</span>
              <Circle
                className="ml-2"
                stroke="none"
                size={12}
                fill={
                  data?.attendance_rate > 70
                    ? "#00C853"
                    : data?.attendance_rate >= 50
                      ? "#FF9800"
                      : "red"
                }
              />
            </div>
          </TableCell>
          <TableCell className="text-gray-900 dark:text-white">
            <div className="flex justify-center items-center">
              {growthRate == null ? (
                <span>-</span>
              ) : (
                <>
                  {growthRate > 0 && (
                    <TrendingUp
                      className="mr-2"
                      stroke="none"
                      size={25}
                      fill={"#00C853"}
                    />
                  )}
                  {growthRate < 0 && (
                    <TrendingDown
                      className="mr-2"
                      stroke="none"
                      size={25}
                      fill={"#C10007"}
                    />
                  )}
                  {growthRate === 0 && <span className="w-[25px] mr-2"></span>}
                  <span>
                    {growthRate > 0 ? `+${growthRate.toFixed(1)}%` : `${growthRate.toFixed(1)}%`}
                  </span>
                </>
              )}
            </div>
          </TableCell>
          {/* <TableCell className="text-center">
                              {data?.adoption_rate >= 70 &&
                                  <span className="border border-[#00C853] text-[#00C853] bg-[#DBFCE7] py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                              {data?.adoption_rate >= 50 && data?.adoption_rate < 70 &&
                                  <span className="border border-[#FF9800] text-[#FF9800] bg-[#FFF3E0] py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                              {data?.adoption_rate < 50 &&
                                  <span className="border border-red-500 text-red-500 bg-red-100 py-1 px-3 rounded-2xl">
                                      {data?.priority}
                                  </span>
                              }
                          </TableCell> */}
        </TableRow>
      );
    });

  return (
    <div className="mt-5 w-full border border-[#E5F6F9] dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
      <div className="px-6 py-6">
        <Row className="mb-2">
          <span className="text-xl block font-semibold text-[#364153] dark:text-gray-100">
            Community Activity Distribution
          </span>
        </Row>
        <Row>
          <span className="block text-[#4A5565] dark:text-gray-400">
            Evaluate the availability and performance of programs, events, and engagement
            opportunities across neighborhoods.
          </span>
        </Row>
      </div>
      <Row className="mt-5 relative">
        <Table
          containerClassName="max-h-[830px] overflow-y-auto scrollbar-hide"
          className="min-w-max"
        >
          <TableHeader className="sticky top-[-5px] z-10 bg-[#F7F8FA] dark:bg-gray-700">
            <TableRow>
              <TableHead className="text-[#364153] dark:text-gray-100 font-semibold">
                Area Name
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-semibold">
                Total Event
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-semibold">
                Live Event (happening)
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-semibold">
                Attendance Rate
              </TableHead>
              <TableHead className="text-center text-[#364153] dark:text-gray-100 font-semibold">
                Growth
              </TableHead>
              {/* <TableHead className="text-center">Priority</TableHead> */}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRowsSkeleton colSpan={5} />
            ) : error ? (
              <TableErrorState
                colSpan={5}
                error={error}
                onRetry={onRetry}
              />
            ) : (
              rows
            )}
          </TableBody>
        </Table>
      </Row>
    </div>
  );
};

export default TargetOpportunityTable;
